import { ExternalLink, Link2, Lock, MoreVertical, Paperclip, Pencil, Send, Trash2, Unlock, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Avatar, Button, Label, Tag } from "./ui";
import { RichText } from "./RichText";
import { canViewCooperationRequest, labelForRequestStatus, labelForRequestType } from "../lib/cooperation";
import { canEditDiscussion, canViewAdmin } from "../lib/domain";
import { createCuratedLegislationFiles } from "../lib/legislativeWatch";
import type { Attachment, CooperationRequest, Discussion, DiscussionContribution, LegislationFile, LexData } from "../lib/types";

export type AttachmentPersistence = {
  generateUploadUrl: () => Promise<string>;
  addDiscussionAttachment: (input: { discussionId: string; storageId: string; filename: string; contentType: string; size: number }) => Promise<unknown>;
  removeAttachment: (attachmentId: string) => Promise<unknown>;
};

export type ModerationPersistence = {
  removeDiscussion: (discussionId: string) => Promise<unknown>;
  removeContribution: (contributionId: string) => Promise<unknown>;
};

export type DiscussionPersistence = {
  addContribution: (input: { discussionId: string; body: string; parentContributionId?: string | null }) => Promise<unknown>;
  closeDiscussion: (input: { discussionId: string; closed: boolean }) => Promise<unknown>;
  pushProposalToVote: (input: { discussionId: string; proposalText: string }) => Promise<{ proposalId: string }>;
};

type DetailKey = "discussion" | "file" | "request";

// Renders the detail view for the `?discussion=`, `?file=` and `?request=` deep
// links used across the app (dashboard, search, notifications, member profiles,
// workspace and legislation). These params previously navigated to a list page
// that ignored them, so the click appeared to do nothing. Mounted once in the
// AppShell so every link works regardless of which route is showing.
export function WorkspaceDetailModals({ data, attachmentPersistence, moderationPersistence, discussionPersistence }: { data: LexData; attachmentPersistence?: AttachmentPersistence; moderationPersistence?: ModerationPersistence; discussionPersistence?: DiscussionPersistence }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const discussionId = searchParams.get("discussion");
  const requestId = searchParams.get("request");
  const fileId = searchParams.get("file");

  const close = (key: DetailKey) => {
    const next = new URLSearchParams(searchParams);
    next.delete(key);
    setSearchParams(next, { replace: true });
  };

  if (discussionId) {
    const discussion = data.discussions.find((item) => item.id === discussionId);
    if (discussion) return <DiscussionDetailModal data={data} discussion={discussion} attachmentPersistence={attachmentPersistence} moderationPersistence={moderationPersistence} discussionPersistence={discussionPersistence} onClose={() => close("discussion")} />;
  }
  if (requestId) {
    const request = data.cooperationRequests.find((item) => item.id === requestId);
    if (request && canViewCooperationRequest(data.viewer, request)) {
      return <CooperationRequestDetailModal data={data} request={request} onClose={() => close("request")} />;
    }
  }
  if (fileId) {
    const file = [...data.legislationFiles, ...createCuratedLegislationFiles(data)].find((item) => item.id === fileId);
    if (file) return <LegislationFileDetailModal data={data} file={file} onClose={() => close("file")} />;
  }
  return null;
}

function DetailShell({ eyebrow, title, description, children, primary, headerMenu, footer, onClose }: {
  eyebrow: string;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  primary?: { label: string; href: string; external?: boolean };
  headerMenu?: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-navy/45 p-0 backdrop-blur-[2px] sm:place-items-center sm:p-4" role="dialog" aria-modal="true">
      <button type="button" aria-label="Close" className="absolute inset-0 h-full w-full cursor-default" onClick={onClose} />
      <div className="relative max-h-[92vh] w-full overflow-auto rounded-t-2xl border border-line bg-elevated p-5 shadow-soft sm:max-w-xl sm:rounded-2xl">
        <div className="mb-4 flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="mono text-[10.5px] font-medium uppercase tracking-[0.13em] text-muted">{eyebrow}</div>
            <h2 className="mt-1 text-xl font-semibold tracking-[-0.02em] text-ink">{title}</h2>
            {description ? <div className="mt-1.5 text-sm leading-6 text-secondary">{description}</div> : null}
          </div>
          {headerMenu ?? null}
          <button type="button" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-line hover:bg-hover" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
        {footer !== undefined ? (
          footer
        ) : (
          <div className="mt-5 flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>Close</Button>
            {primary ? (
              primary.external ? (
                <a href={primary.href} target="_blank" rel="noreferrer" onClick={onClose} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white shadow-[0_4px_12px_rgba(122,28,44,0.22)] hover:brightness-95">
                  {primary.label}
                  <ExternalLink className="h-4 w-4" />
                </a>
              ) : (
                <Link to={primary.href} onClick={onClose} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white shadow-[0_4px_12px_rgba(122,28,44,0.22)] hover:brightness-95">
                  {primary.label}
                </Link>
              )
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function DiscussionDetailModal({ data, discussion, attachmentPersistence, moderationPersistence, discussionPersistence, onClose }: { data: LexData; discussion: Discussion; attachmentPersistence?: AttachmentPersistence; moderationPersistence?: ModerationPersistence; discussionPersistence?: DiscussionPersistence; onClose: () => void }) {
  const navigate = useNavigate();
  const author = data.users.find((user) => user.id === discussion.createdByUserId);
  const firm = data.firms.find((item) => item.id === discussion.createdByFirmId);
  const isMine = discussion.createdByUserId === data.viewer.id;
  const [removedReplyIds, setRemovedReplyIds] = useState<string[]>([]);
  const [removingPost, setRemovingPost] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [childText, setChildText] = useState("");
  const [posting, setPosting] = useState(false);
  const [pushingToVote, setPushingToVote] = useState(false);
  const [voteError, setVoteError] = useState<string | null>(null);
  const replies = data.contributions
    .filter((item) => item.discussionId === discussion.id && !removedReplyIds.includes(item.id))
    .sort((a, b) => a.createdAt - b.createdAt);
  const topReplies = replies.filter((reply) => !reply.parentContributionId);
  const childrenOf = (id: string) => replies.filter((reply) => reply.parentContributionId === id);
  const attachments = (data.attachments ?? []).filter((item) => item.entityType === "discussion" && item.entityId === discussion.id);
  const canManageAttachments = canEditDiscussion(data.viewer, discussion);
  const isAdmin = canViewAdmin(data.viewer);
  const closed = discussion.status === "closed";
  const canParticipate = data.viewer.memberCategory !== "observer";
  const canReply = Boolean(discussionPersistence) && !closed && canParticipate;
  const canClose = Boolean(discussionPersistence) && (isAdmin || isMine);
  const canPushToVote = Boolean(discussionPersistence) && discussion.postType === "Proposal" && !discussion.linkedGovernanceProposalId && canParticipate;

  const postReply = async (parentContributionId: string | null, text: string, reset: () => void) => {
    if (!discussionPersistence || !text.trim()) return;
    setPosting(true);
    try {
      await discussionPersistence.addContribution({ discussionId: discussion.id, body: text.trim(), parentContributionId });
      reset();
      setReplyingTo(null);
    } finally {
      setPosting(false);
    }
  };
  const toggleClose = async () => {
    if (!discussionPersistence) return;
    try { await discussionPersistence.closeDiscussion({ discussionId: discussion.id, closed: !closed }); } catch { /* keep state on failure */ }
  };  const pushToVote = async () => {
    if (!discussionPersistence) return;
    setPushingToVote(true);
    setVoteError(null);
    try {
      const result = await discussionPersistence.pushProposalToVote({ discussionId: discussion.id, proposalText: discussion.body });
      onClose();
      navigate(`/discussions/proposals/${result.proposalId}`);
    } catch (caught) {
      setVoteError(caught instanceof Error ? caught.message : "The proposal could not be pushed to a vote.");
      setPushingToVote(false);
    }
  };

  const renderReply = (reply: DiscussionContribution, isChild: boolean) => {
    const replyAuthor = data.users.find((user) => user.id === reply.createdByUserId);
    const replyFirm = data.firms.find((item) => item.id === reply.createdByFirmId);
    return (
      <div className={`rounded-lg border border-divider bg-canvas p-3 text-sm ${isChild ? "ml-8" : ""}`}>
        <div className="flex items-center gap-2 text-xs text-secondary">
          <Avatar name={replyAuthor?.name ?? "Member"} imageUrl={replyAuthor?.avatarUrl} size="sm" />
          <span className="font-medium text-ink">{reply.createdByUserId === data.viewer.id ? "You" : replyAuthor?.name ?? "Member"}</span>
          <span>{replyFirm?.name}</span>
          <span>· {relativeAge(reply.createdAt)}</span>
          {isAdmin && moderationPersistence ? (
            <button type="button" className="ml-auto grid h-7 w-7 place-items-center rounded-lg hover:bg-hover" aria-label="Remove comment" onClick={async () => {
              if (!window.confirm("Remove this comment?")) return;
              try { await moderationPersistence.removeContribution(reply.id); setRemovedReplyIds((current) => [...current, reply.id]); } catch { /* leave in place */ }
            }}><Trash2 className="h-3.5 w-3.5 text-secondary" /></button>
          ) : null}
        </div>
        <div className="mt-1.5 whitespace-pre-wrap leading-6 text-secondary">{reply.body}</div>
        {!isChild && canReply ? (
          replyingTo === reply.id ? (
            <div className="mt-2"><ReplyComposer value={childText} onChange={setChildText} posting={posting} placeholder="Write a reply…" onSubmit={() => void postReply(reply.id, childText, () => setChildText(""))} onCancel={() => { setReplyingTo(null); setChildText(""); }} /></div>
          ) : (
            <button type="button" className="mt-2 text-xs font-semibold text-primary hover:underline" onClick={() => { setReplyingTo(reply.id); setChildText(""); }}>Reply</button>
          )
        ) : null}
      </div>
    );
  };

  const removePost = async () => {
    if (!moderationPersistence) return;
    if (!window.confirm("Remove this post for everyone? This permanently deletes it and its replies.")) return;
    setRemovingPost(true);
    try {
      await moderationPersistence.removeDiscussion(discussion.id);
      onClose();
    } catch {
      setRemovingPost(false);
    }
  };
  const primary = discussion.linkedGovernanceProposalId
    ? { label: "Open vote", href: `/discussions/proposals/${discussion.linkedGovernanceProposalId}` }
    : discussion.status === "drafting" && isMine
      ? { label: "Continue draft", href: `/discussions?compose=1&draft=${discussion.id}` }
      : undefined;

  const canModerate = isAdmin && Boolean(moderationPersistence);
  const canEdit = canEditDiscussion(data.viewer, discussion);
  const canAttach = Boolean(attachmentPersistence) && canManageAttachments;
  const showComposer = canReply || canAttach;
  const authorUser = isMine ? data.viewer : author;
  const audienceLabel = discussion.confidentialityLevel === "Full Members only" ? "Full Members only" : "Posted to the network";
  const deepLink = `${typeof window !== "undefined" ? window.location.origin : ""}/discussions?discussion=${discussion.id}`;

  const headerMenu = (
    <DiscussionMenu
      canEdit={canEdit}
      canClose={canClose}
      canModerate={canModerate}
      closed={closed}
      removingPost={removingPost}
      editHref={`/discussions?compose=1&draft=${discussion.id}`}
      deepLink={deepLink}
      onEdit={onClose}
      onToggleClose={() => void toggleClose()}
      onRemovePost={() => void removePost()}
    />
  );

  const footer = (
    <div className="sticky bottom-0 -mx-5 -mb-5 mt-5 border-t border-line bg-elevated px-5 py-4">
      {primary ? (
        <Link to={primary.href} onClick={onClose} className="mb-3 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white shadow-[0_4px_12px_rgba(122,28,44,0.22)] hover:brightness-95">
          {primary.label}
        </Link>
      ) : null}
      {canPushToVote ? (
        <div className="mb-3">
          <Button type="button" disabled={pushingToVote} onClick={() => void pushToVote()}>
            {pushingToVote ? "Opening vote…" : "Push proposal to vote"}
          </Button>
          {voteError ? <div className="mt-2 text-sm font-medium text-red-700">{voteError}</div> : null}
        </div>
      ) : null}
      {showComposer ? (
        <UnifiedComposer discussionId={discussion.id} canReply={canReply} canAttach={canAttach} discussionPersistence={discussionPersistence} attachmentPersistence={attachmentPersistence} />
      ) : (
        <div className="rounded-lg border border-divider bg-canvas px-3 py-2 text-sm text-secondary">
          {closed ? "This thread is closed to new replies." : !canParticipate ? "Observers can read the discussion — full members can reply." : "Open the live workspace to reply."}
        </div>
      )}
    </div>
  );

  return (
    <DetailShell
      eyebrow={discussion.postType ?? "Discussion"}
      title={discussion.title}
      description={
        <span className="flex items-center gap-2.5">
          <Avatar name={authorUser?.name ?? "Member"} imageUrl={authorUser?.avatarUrl} size="sm" />
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="flex items-center gap-2">
              <span className="font-medium text-ink">{isMine ? data.viewer.name : author?.name ?? "Member"}</span>
              {isMine ? (
                <span className="rounded-md bg-surfaceAlt px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary">You</span>
              ) : firm?.name ? (
                <span className="truncate text-[12.5px] text-secondary">· {firm.name}</span>
              ) : null}
            </span>
            <span className="text-[12px] text-muted">{audienceLabel}</span>
          </span>
        </span>
      }
      headerMenu={headerMenu}
      footer={footer}
      onClose={onClose}
    >
      <div className="mb-4 flex flex-wrap gap-2">
        <Tag>{discussion.workflowStatus ?? discussion.status}</Tag>
        {discussion.confidentialityLevel ? <Tag>{discussion.confidentialityLevel}</Tag> : null}
        {(discussion.topicTags ?? []).slice(0, 3).map((topic) => <Tag key={topic}>{topic}</Tag>)}
      </div>
      <RichText markdown={discussion.body} />

      {closed ? (
        <div className="mt-4 inline-flex items-center gap-2 rounded-lg border border-divider bg-canvas px-3 py-1.5 text-[12px] text-secondary">
          <Lock className="h-3.5 w-3.5" /> This thread is closed to new replies.
        </div>
      ) : null}

      {attachments.length > 0 ? (
        <div className="mt-5 border-t border-divider pt-4">
          <Label>Documents · {attachments.length}</Label>
          <div className="mt-3 grid gap-2">
            {attachments.map((attachment) => (
              <AttachmentRow key={attachment.id} attachment={attachment} canRemove={canAttach ? attachment.uploadedByUserId === data.viewer.id : false} persistence={attachmentPersistence} />
            ))}
          </div>
        </div>
      ) : null}

      {replies.length > 0 ? (
        <div className="mt-5 border-t border-divider pt-4">
          <Label>{replies.length} {replies.length === 1 ? "reply" : "replies"}</Label>
          <div className="mt-3 grid gap-2">
            {topReplies.map((reply) => (
              <div key={reply.id} className="grid gap-2">
                {renderReply(reply, false)}
                {childrenOf(reply.id).map((child) => <div key={child.id}>{renderReply(child, true)}</div>)}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </DetailShell>
  );
}

// Header overflow menu: author actions (edit, copy link) + admin moderation (close thread, remove post),
// replacing the old inline "Manage this post" box.
function DiscussionMenu({ canEdit, canClose, canModerate, closed, removingPost, editHref, deepLink, onEdit, onToggleClose, onRemovePost }: {
  canEdit: boolean;
  canClose: boolean;
  canModerate: boolean;
  closed: boolean;
  removingPost: boolean;
  editHref: string;
  deepLink: string;
  onEdit: () => void;
  onToggleClose: () => void;
  onRemovePost: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => { if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false); };
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(deepLink); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* clipboard unavailable */ }
  };
  const itemClass = "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-ink hover:bg-hover";
  const sectionClass = "px-2.5 pb-1 pt-1.5 mono text-[10px] font-semibold uppercase tracking-[0.13em] text-muted";
  const showModeration = canClose || canModerate;
  return (
    <div className="relative" ref={ref}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label="Post actions" onClick={() => setOpen((value) => !value)} className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg border ${open ? "border-ink bg-hover" : "border-line hover:bg-hover"}`}>
        <MoreVertical className="h-4 w-4" />
      </button>
      {open ? (
        <div role="menu" className="absolute right-0 top-[calc(100%+8px)] z-50 w-60 rounded-xl border border-line bg-elevated p-1.5 shadow-soft">
          <div className={sectionClass}>{canEdit ? "Author" : "Post"}</div>
          {canEdit ? (
            <Link to={editHref} role="menuitem" className={itemClass} onClick={() => { setOpen(false); onEdit(); }}>
              <Pencil className="h-4 w-4 text-secondary" /> Edit post
            </Link>
          ) : null}
          <button type="button" role="menuitem" className={itemClass} onClick={() => void copyLink()}>
            <Link2 className="h-4 w-4 text-secondary" /> {copied ? "Link copied" : "Copy link"}
          </button>
          {showModeration ? (
            <>
              <div className="mx-1 my-1 border-t border-divider" />
              <div className={sectionClass}>Admin moderation</div>
              {canClose ? (
                <button type="button" role="menuitem" className={itemClass} onClick={() => { setOpen(false); onToggleClose(); }}>
                  {closed ? <Unlock className="h-4 w-4 text-secondary" /> : <Lock className="h-4 w-4 text-secondary" />} {closed ? "Reopen thread" : "Close thread"}
                </button>
              ) : null}
              {canModerate ? (
                <button type="button" role="menuitem" disabled={removingPost} className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-bad hover:bg-badBg disabled:opacity-50" onClick={() => { setOpen(false); onRemovePost(); }}>
                  <Trash2 className="h-4 w-4" /> {removingPost ? "Removing…" : "Remove post"}
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// One composer for both replying and attaching: type a reply and/or attach a document, then send.
function UnifiedComposer({ discussionId, canReply, canAttach, discussionPersistence, attachmentPersistence }: {
  discussionId: string;
  canReply: boolean;
  canAttach: boolean;
  discussionPersistence?: DiscussionPersistence;
  attachmentPersistence?: AttachmentPersistence;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canSend = (((canReply && text.trim().length > 0) || (canAttach && file !== null))) && !busy;
  const placeholder = canReply && canAttach ? "Add a reply or attach a document…" : canReply ? "Add a reply…" : "Attach a document…";

  const submit = async () => {
    if (!canSend) return;
    setBusy(true);
    setError(null);
    try {
      if (file && canAttach && attachmentPersistence) {
        const uploadUrl = await attachmentPersistence.generateUploadUrl();
        const response = await fetch(uploadUrl, { method: "POST", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file });
        if (!response.ok) throw new Error("Upload failed.");
        const { storageId } = (await response.json()) as { storageId: string };
        await attachmentPersistence.addDiscussionAttachment({ discussionId, storageId, filename: file.name, contentType: file.type || "application/octet-stream", size: file.size });
      }
      if (text.trim() && canReply && discussionPersistence) {
        await discussionPersistence.addContribution({ discussionId, body: text.trim() });
      }
      setText("");
      setFile(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Your message could not be posted.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      {file ? (
        <div className="mb-2 flex items-center gap-2 rounded-lg border border-divider bg-canvas px-2.5 py-1.5 text-[12px] text-secondary">
          <Paperclip className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-ink">{file.name}</span>
          <span>{formatBytes(file.size)}</span>
          <button type="button" aria-label="Remove attachment" onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ""; }} className="grid h-6 w-6 place-items-center rounded-md hover:bg-hover"><X className="h-3.5 w-3.5" /></button>
        </div>
      ) : null}
      <div className="flex items-end gap-1 rounded-xl border border-line bg-surface px-2 py-1.5 focus-within:border-primary">
        <textarea
          rows={1}
          disabled={!canReply}
          className="max-h-32 min-h-[28px] flex-1 resize-none bg-transparent px-1.5 py-1.5 text-sm leading-6 text-ink outline-none placeholder:text-muted disabled:cursor-not-allowed"
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={placeholder}
          onKeyDown={(event) => { if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) { event.preventDefault(); void submit(); } }}
        />
        {canAttach ? (
          <>
            <input ref={inputRef} type="file" className="hidden" onChange={(event) => { const picked = event.target.files?.[0]; if (picked) setFile(picked); }} />
            <button type="button" aria-label="Attach a document" onClick={() => inputRef.current?.click()} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-secondary hover:bg-hover">
              <Paperclip className="h-4 w-4" />
            </button>
          </>
        ) : null}
        <button type="button" disabled={!canSend} onClick={() => void submit()} aria-label="Send" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary text-white hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50">
          <Send className="h-4 w-4" />
        </button>
      </div>
      {error ? <div className="mt-1.5 text-[12px] text-bad">{error}</div> : null}
    </div>
  );
}

function ReplyComposer({ value, onChange, posting, placeholder, onSubmit, onCancel }: { value: string; onChange: (value: string) => void; posting: boolean; placeholder: string; onSubmit: () => void; onCancel?: () => void }) {
  return (
    <div className="flex items-end gap-2">
      <textarea
        rows={2}
        className="min-h-11 flex-1 rounded-lg border border-line bg-surface px-3 py-2 text-sm leading-6 text-ink outline-none focus:border-primary"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        onKeyDown={(event) => { if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) { event.preventDefault(); onSubmit(); } }}
      />
      <button type="button" disabled={posting || !value.trim()} onClick={onSubmit} aria-label="Send reply" className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-primary text-white hover:bg-[#6a1925] disabled:cursor-not-allowed disabled:opacity-50">
        <Send className="h-4 w-4" />
      </button>
      {onCancel ? <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button> : null}
    </div>
  );
}

function CooperationRequestDetailModal({ data, request, onClose }: { data: LexData; request: CooperationRequest; onClose: () => void }) {
  const isInbound = request.targetFirmId === data.viewerFirm?.id;
  const counterpartyId = isInbound ? request.requesterFirmId : request.targetFirmId;
  const counterparty = data.firms.find((item) => item.id === counterpartyId);
  const practiceNames = data.practiceAreas.filter((area) => request.practiceAreaIds.includes(area.id)).map((area) => area.name);
  const confidential = request.confidentialDetailsAdded || request.clientDisclosed;

  return (
    <DetailShell
      eyebrow={labelForRequestType(request.requestType)}
      title={request.title}
      description={`${isInbound ? "From" : "To"} ${counterparty?.name ?? "Member firm"}${counterparty ? ` · ${counterparty.jurisdiction}` : ""}`}
      primary={counterparty ? { label: "View member firm", href: `/members/${counterparty.id}` } : undefined}
      onClose={onClose}
    >
      <div className="mb-4 flex flex-wrap gap-2">
        <Tag>{labelForRequestStatus(request.status)}</Tag>
        <Tag>{request.urgency.replace(/_/g, " ")}</Tag>
        {request.conflictCheckRequired ? <Tag>conflict check required</Tag> : null}
      </div>
      <p className="text-[15px] leading-6 text-ink">{request.shortSummary}</p>
      <div className="mt-4 grid gap-2 text-sm text-secondary">
        {request.jurisdictionTags.length > 0 ? <div>Jurisdictions — {request.jurisdictionTags.join(", ")}</div> : null}
        {practiceNames.length > 0 ? <div>Practice areas — {practiceNames.join(", ")}</div> : null}
        {confidential ? <div className="rounded-lg border border-line bg-canvas px-3 py-2">Confidential details are shared only between the requesting and responding firms.</div> : null}
      </div>
    </DetailShell>
  );
}

function LegislationFileDetailModal({ data, file, onClose }: { data: LexData; file: LegislationFile; onClose: () => void }) {
  const owner = data.firms.find((firm) => firm.id === file.ownerFirmId)?.name ?? "LexMosaic";
  const areas = file.practiceAreaIds
    .map((id) => data.practiceAreas.find((area) => area.id === id)?.name)
    .filter(Boolean) as string[];
  const relatedProjects = data.lobbyingProjects.filter((project) => project.legislationFileId === file.id);

  return (
    <DetailShell
      eyebrow="Legislative file"
      title={file.title}
      description={`${file.jurisdictionOrInstitution} · ${file.stage}`}
      primary={file.sourceUrl ? { label: "Open source", href: file.sourceUrl, external: true } : undefined}
      onClose={onClose}
    >
      <div className="mb-4 flex flex-wrap gap-2">
        <Tag>{file.stage}</Tag>
        <Tag>{file.status}</Tag>
        {areas.slice(0, 3).map((area) => <Tag key={area}>{area}</Tag>)}
      </div>
      <p className="text-[15px] leading-6 text-ink">{file.summary}</p>
      <div className="mt-4 grid gap-2 text-sm text-secondary">
        <div>Coordinator — {owner}</div>
        <div>Deadline — {deadlineLabel(file.deadline)}</div>
      </div>
      {relatedProjects.length > 0 ? (
        <div className="mt-5 border-t border-divider pt-4">
          <Label>Joint positions</Label>
          <div className="mt-3 grid gap-2">
            {relatedProjects.map((project) => (
              <div key={project.id} className="rounded-lg border border-divider bg-canvas p-3 text-sm">
                <div className="font-medium text-ink">{project.title}</div>
                <div className="mt-1 text-secondary">{project.objective}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </DetailShell>
  );
}

function AttachmentRow({ attachment, canRemove, persistence }: { attachment: Attachment; canRemove: boolean; persistence?: AttachmentPersistence }) {
  const [removing, setRemoving] = useState(false);
  const [removed, setRemoved] = useState(false);
  if (removed) return null;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-divider bg-canvas px-3 py-2 text-sm">
      <Paperclip className="h-4 w-4 shrink-0 text-muted" />
      <div className="min-w-0 flex-1">
        {attachment.url ? (
          <a href={attachment.url} target="_blank" rel="noreferrer" className="block truncate font-medium text-ink hover:underline">{attachment.filename}</a>
        ) : (
          <span className="block truncate font-medium text-ink">{attachment.filename}</span>
        )}
        <span className="text-[12px] text-secondary">{formatBytes(attachment.size)}</span>
      </div>
      {canRemove && persistence ? (
        <button
          type="button"
          className="grid h-8 w-8 place-items-center rounded-lg hover:bg-hover"
          aria-label="Remove document"
          disabled={removing}
          onClick={async () => {
            setRemoving(true);
            try {
              await persistence.removeAttachment(attachment.id);
              setRemoved(true);
            } catch {
              setRemoving(false);
            }
          }}
        >
          <Trash2 className="h-4 w-4 text-secondary" />
        </button>
      ) : null}
    </div>
  );
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function deadlineLabel(deadline: number | null | undefined) {
  if (!deadline) return "Ongoing";
  const days = Math.round((deadline - Date.now()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return "Due";
  return `in ${days} days`;
}

function relativeAge(time: number) {
  const abs = Math.abs(time - Date.now());
  const hour = 60 * 60 * 1000;
  if (abs < hour) return "just now";
  if (abs < 24 * hour) return `${Math.max(1, Math.round(abs / hour))}h ago`;
  return `${Math.max(1, Math.round(abs / (24 * hour)))}d ago`;
}
